export type EdgelessImportFileType =
  | 'csv'
  | 'excel'
  | 'markdown'
  | 'pdf'
  | 'docx'
  | 'html'
  | 'zip';

export type EdgelessImportChoice =
  | 'attachment'
  | 'database'
  | 'document'
  | 'notes'
  | 'pages'
  | 'html'
  | 'markdownZip'
  | 'notionEnhanced'
  | 'snapshot';

export type EdgelessImportDialogType =
  | 'csv'
  | 'excel'
  | 'html'
  | 'markdownZip'
  | 'notionEnhanced'
  | 'snapshot';

export type EdgelessImportUnsupportedReason = 'legacy-xls';

export interface EdgelessImportFileLike {
  name: string;
  type?: string;
}

export interface EdgelessImportSupportedClassification {
  status: 'supported';
  fileType: EdgelessImportFileType;
  fileLabel: string;
  importChoice: Exclude<EdgelessImportChoice, 'attachment'>;
  importDialogType?: EdgelessImportDialogType;
  choices: readonly EdgelessImportChoice[];
}

export interface EdgelessImportUnsupportedClassification {
  status: 'unsupported';
  reason: EdgelessImportUnsupportedReason;
  fileLabel: string;
}

export type EdgelessImportClassification =
  | EdgelessImportSupportedClassification
  | EdgelessImportUnsupportedClassification;

export interface EdgelessImportSelection<TFile extends EdgelessImportFileLike> {
  files: TFile[];
  classification: EdgelessImportSupportedClassification;
}

export interface EdgelessDragItemLike {
  kind?: string;
  type?: string;
}

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const DRAG_ITEM_MIME_TYPES = new Set([
  'application/csv',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/xhtml+xml',
  DOCX_MIME,
  XLSX_MIME,
  'text/comma-separated-values',
  'text/csv',
  'text/html',
  'text/markdown',
  'text/x-markdown',
]);

const SUPPORTED_CLASSIFICATIONS: Record<
  EdgelessImportFileType,
  EdgelessImportSupportedClassification
> = {
  csv: {
    status: 'supported',
    fileType: 'csv',
    fileLabel: 'CSV',
    importChoice: 'database',
    importDialogType: 'csv',
    choices: ['attachment', 'database'],
  },
  excel: {
    status: 'supported',
    fileType: 'excel',
    fileLabel: 'Excel',
    importChoice: 'database',
    importDialogType: 'excel',
    choices: ['attachment', 'database'],
  },
  markdown: {
    status: 'supported',
    fileType: 'markdown',
    fileLabel: 'Markdown',
    importChoice: 'notes',
    choices: ['attachment', 'notes'],
  },
  pdf: {
    status: 'supported',
    fileType: 'pdf',
    fileLabel: 'PDF',
    importChoice: 'pages',
    choices: ['attachment', 'pages'],
  },
  docx: {
    status: 'supported',
    fileType: 'docx',
    fileLabel: 'DOCX',
    importChoice: 'document',
    choices: ['document', 'attachment'],
  },
  html: {
    status: 'supported',
    fileType: 'html',
    fileLabel: 'HTML',
    importChoice: 'html',
    importDialogType: 'html',
    choices: ['attachment', 'html'],
  },
  zip: {
    status: 'supported',
    fileType: 'zip',
    fileLabel: 'ZIP',
    importChoice: 'markdownZip',
    choices: ['attachment', 'markdownZip', 'notionEnhanced', 'snapshot'],
  },
};

const IMPORT_PRIORITY: readonly EdgelessImportFileType[] = [
  'pdf',
  'docx',
  'markdown',
  'html',
  'csv',
  'excel',
  'zip',
];

const normalizeFileType = (type?: string) => type?.trim().toLowerCase() ?? '';

const normalizeFileName = (name: string) => name.trim().toLowerCase();

export const classifyEdgelessImportFile = (
  file: EdgelessImportFileLike
): EdgelessImportClassification | null => {
  const type = normalizeFileType(file.type);
  const name = normalizeFileName(file.name);

  if (type === 'application/pdf' || name.endsWith('.pdf')) {
    return SUPPORTED_CLASSIFICATIONS.pdf;
  }

  if (type === DOCX_MIME || name.endsWith('.docx')) {
    return SUPPORTED_CLASSIFICATIONS.docx;
  }

  if (
    type === 'text/markdown' ||
    type === 'text/x-markdown' ||
    name.endsWith('.md') ||
    name.endsWith('.markdown')
  ) {
    return SUPPORTED_CLASSIFICATIONS.markdown;
  }

  if (
    type === 'text/html' ||
    type === 'application/xhtml+xml' ||
    name.endsWith('.html') ||
    name.endsWith('.htm')
  ) {
    return SUPPORTED_CLASSIFICATIONS.html;
  }

  if (
    type === 'text/csv' ||
    type === 'application/csv' ||
    type === 'text/comma-separated-values' ||
    name.endsWith('.csv')
  ) {
    return SUPPORTED_CLASSIFICATIONS.csv;
  }

  if (type === XLSX_MIME || name.endsWith('.xlsx')) {
    return SUPPORTED_CLASSIFICATIONS.excel;
  }

  if (name.endsWith('.xls')) {
    return {
      status: 'unsupported',
      reason: 'legacy-xls',
      fileLabel: 'Excel',
    };
  }

  if (
    type === 'application/zip' ||
    type === 'application/x-zip-compressed' ||
    name.endsWith('.zip')
  ) {
    return SUPPORTED_CLASSIFICATIONS.zip;
  }

  return null;
};

export const getEdgelessImportChoices = (
  fileType: EdgelessImportFileType
): readonly EdgelessImportChoice[] =>
  SUPPORTED_CLASSIFICATIONS[fileType].choices;

export const getEdgelessImportTarget = (
  fileType: EdgelessImportFileType
): Exclude<EdgelessImportChoice, 'attachment'> =>
  SUPPORTED_CLASSIFICATIONS[fileType].importChoice;

export const getEdgelessImportFileLabel = (fileType: EdgelessImportFileType) =>
  SUPPORTED_CLASSIFICATIONS[fileType].fileLabel;

export const getEdgelessDialogImportType = (
  choice: EdgelessImportChoice,
  fileType: EdgelessImportFileType
): EdgelessImportDialogType | null => {
  if (choice === 'database') {
    return fileType === 'csv' || fileType === 'excel' ? fileType : null;
  }

  if (choice === 'html') {
    return 'html';
  }

  if (
    choice === 'markdownZip' ||
    choice === 'notionEnhanced' ||
    choice === 'snapshot'
  ) {
    return choice;
  }

  return null;
};

export const selectPrimaryEdgelessImportFiles = <
  TFile extends EdgelessImportFileLike,
>(
  files: readonly TFile[]
): EdgelessImportSelection<TFile> | null => {
  const grouped = new Map<EdgelessImportFileType, TFile[]>();

  for (const file of files) {
    const classification = classifyEdgelessImportFile(file);
    if (!classification || classification.status !== 'supported') continue;

    const current = grouped.get(classification.fileType) ?? [];
    current.push(file);
    grouped.set(classification.fileType, current);
  }

  for (const fileType of IMPORT_PRIORITY) {
    const importFiles = grouped.get(fileType);
    if (!importFiles?.length) continue;

    return {
      files: fileType === 'zip' ? importFiles.slice(0, 1) : importFiles,
      classification: SUPPORTED_CLASSIFICATIONS[fileType],
    };
  }

  return null;
};

export const isPotentiallyImportableEdgelessDragItem = (
  item: EdgelessDragItemLike
) => {
  if (item.kind && item.kind !== 'file') return false;

  const type = normalizeFileType(item.type);
  return type === '' || DRAG_ITEM_MIME_TYPES.has(type);
};
